import { FieldError } from "@design-system/shadcn";
import React, { useMemo } from "react";
import { z } from "zod";

import { useFieldContext } from "../core/form-contexts";

type FormFieldErrorProps = Omit<
  React.ComponentProps<typeof FieldError>,
  "errors" | "children"
>;

// Helper function to extract error messages from zod error objects or strings
const extractErrorMessage = (error: string | z.ZodIssue): string => {
  if (typeof error === "string") {
    return error;
  }
  // ZodIssue has a message property
  return error.message;
};

function FormFieldError(props: FormFieldErrorProps) {
  const field = useFieldContext<unknown>();

  if (!field) {
    throw new Error("FormFieldError must be used within form.AppField");
  }

  const errors = useMemo(
    () =>
      field.state.meta.errors.map((e) => ({
        message: extractErrorMessage(e as string | z.ZodIssue),
      })),
    [field.state.meta.errors]
  );

  if (errors.length === 0) {
    return null;
  }

  return <FieldError errors={errors} {...props} />;
}

export { FormFieldError };
export type { FormFieldErrorProps };
